import { useState, useEffect, useCallback, RefObject } from 'react'
import { useViewport } from '@xyflow/react'

interface ViewportBounds {
  x: number
  y: number
  width: number
  height: number
}

/**
 * A hook to track the visible viewport bounds, accounting for both zoom and pan actions.
 * This hook will update the viewport when:
 * 1. The user scrolls or resizes the window
 * 2. The user zooms in or out
 * 3. The user pans the document view
 */
export default function useVisibleViewport(
  containerRef: RefObject<HTMLElement>,
  zoom = 1,
): ViewportBounds | undefined {
  const [viewport, setViewport] = useState<ViewportBounds | undefined>(
    undefined,
  )

  // Use React Flow's useViewport hook to get the current viewport including x,y position
  // This provides real-time updates for panning and zooming
  const { x: flowX, y: flowY, zoom: flowZoom } = useViewport()

  // Use the actual flow zoom value if provided
  const effectiveZoom = flowZoom || zoom

  const updateViewport = useCallback(() => {
    if (!containerRef.current) return

    const container = containerRef.current

    // Special fix for XYFlow nodes
    const parentNode = container.parentElement
    if (!parentNode) return

    // Find the scrolling container (usually the XYFlow pane)
    const xyflowPane = document.querySelector('.react-flow__pane')
    if (!xyflowPane) return

    // Get the absolute position in the document
    const xyflowRect = xyflowPane.getBoundingClientRect()

    // Calculate viewport bounds in document coordinates
    // React Flow's coordinate system requires careful handling of transformations

    // Calculate the viewport bounds based on zoom level
    const viewportBounds = {
      // Convert screen coordinates to document coordinates
      // For high zoom levels, we adjust the calculation to ensure better precision
      x: -flowX / effectiveZoom,
      y: -flowY / effectiveZoom,
      // Use the actual viewport width/height, adjusted for zoom
      width: xyflowRect.width / effectiveZoom,
      height: xyflowRect.height / effectiveZoom,
    }

    // Ensure we don't have negative coordinates
    if (viewportBounds.x < 0) viewportBounds.x = 0
    if (viewportBounds.y < 0) viewportBounds.y = 0

    setViewport(viewportBounds)
  }, [containerRef, effectiveZoom, flowX, flowY])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Initial calculation
    updateViewport()

    // Update on scroll and resize
    document.addEventListener('scroll', updateViewport, true)
    window.addEventListener('resize', updateViewport)

    return () => {
      document.removeEventListener('scroll', updateViewport, true)
      window.removeEventListener('resize', updateViewport)
    }
  }, [containerRef, updateViewport])

  // React when flowX, flowY, or flowZoom changes (panning or zooming)
  // This effect ensures updates happen when the user pans the document
  useEffect(() => {
    // Immediate update when panning or zooming occurs
    updateViewport()
  }, [flowX, flowY, flowZoom, updateViewport])

  return viewport
}
